// M2 Slice 9 — Super Admin legal-management API helper + pure UI logic (frontend-only).
// Uses the existing authenticated Axios `api` instance. No history / no source download here.
import { api } from "./api";

const DOCX_EXT = ".docx";
const PDF_EXT = ".pdf";
const MAX_BYTES = 5 * 1024 * 1024; // 5 MiB

// India identifier structures — mirror backend schemas.py (_PAN_RE / _CIN_RE / _GSTIN_RE).
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const CIN_RE = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------- pure helpers
export function acceptExtForKind(kind) {
  return kind === "PUBLIC_POLICY" ? PDF_EXT : DOCX_EXT; // dynamic → .docx, policy → .pdf
}

// Validate a selected File against the category kind. Returns { ok, error }.
export function validateSelectedFile(file, kind) {
  if (!file) return { ok: false, error: "Select a file to publish." };
  const ext = acceptExtForKind(kind);
  const name = (file.name || "").toLowerCase();
  if (!name.endsWith(ext)) {
    return { ok: false, error: `This category requires a ${ext.toUpperCase().slice(1)} file (${ext}).` };
  }
  if (file.size > MAX_BYTES) {
    return { ok: false, error: "File is larger than 5 MiB." };
  }
  return { ok: true, error: null };
}

export function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

// "D Month YYYY" from an ISO timestamp, using the Asia/Kolkata (IST) calendar date — legal date
// convention. Date-only (no time-of-day), so no IST suffix is shown.
export function formatLegalDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", day: "numeric", month: "long", year: "numeric",
  }).format(d);
}

// Decide whether a failed publication attempt may be retried with the SAME idempotency key.
// error is an Axios error (or a synthetic { isNetworkError: true } / timeout).
export function isRetryableFailure(error) {
  if (!error) return false;
  // Uncertain network / timeout (commit status unknown) → same-key manual retry is safe.
  if (error.code === "ECONNABORTED" || error.isNetworkError || !error.response) return true;
  const detail = error.response.data && error.response.data.detail;
  if (detail && typeof detail === "object" && typeof detail.retryable === "boolean") {
    return detail.retryable;
  }
  return false;
}

// A server validation failure (422 with PUBLICATION_VALIDATION_FAILED) forces a corrected file.
export function isValidationFailure(error) {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  return !!(error && error.response && error.response.status === 422 &&
    detail && detail.reason_code === "PUBLICATION_VALIDATION_FAILED");
}

export function newIdempotencyKey() {
  return crypto.randomUUID();
}

// Trim + uppercase Indian legal identifiers (PAN/CIN/GSTIN).
export function normalizeIdentifier(v) {
  return (v || "").trim().toUpperCase();
}

// Client-side mirror of the backend LegalEntitySettingsReplaceIn validation. Returns { ok, errors }.
export function validateEntitySettings(form) {
  const errors = {};
  const name = (form.legal_company_name || "").trim();
  const addr = (form.registered_office_address || "").trim();
  const pan = normalizeIdentifier(form.pan);
  const cin = normalizeIdentifier(form.cin);
  const gstin = normalizeIdentifier(form.gstin);
  const email = (form.legal_support_email || "").trim();
  if (!name) errors.legal_company_name = "Required.";
  if (!addr) errors.registered_office_address = "Required.";
  if (!PAN_RE.test(pan)) errors.pan = "Invalid PAN format.";
  if (!CIN_RE.test(cin)) errors.cin = "Invalid CIN format.";
  if (gstin && !GSTIN_RE.test(gstin)) errors.gstin = "Invalid GSTIN format.";
  if (!EMAIL_RE.test(email)) errors.legal_support_email = "Invalid email.";
  return { ok: Object.keys(errors).length === 0, errors };
}

// Build the full PUT payload; blank GSTIN → null (cleared).
export function buildEntityPayload(form) {
  const gstin = normalizeIdentifier(form.gstin);
  return {
    legal_company_name: (form.legal_company_name || "").trim(),
    pan: normalizeIdentifier(form.pan),
    cin: normalizeIdentifier(form.cin),
    registered_office_address: (form.registered_office_address || "").trim(),
    gstin: gstin === "" ? null : gstin,
    legal_support_email: (form.legal_support_email || "").trim(),
  };
}

// ---------------------------------------------------------------- API calls
export async function getLegalCategories() {
  const { data } = await api.get("/legal-admin/categories");
  return data;
}

export async function publishLegalVersion(categoryKey, file, idempotencyKey) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/legal-admin/categories/${categoryKey}/versions`, fd,
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

export async function getEntitySettings() {
  const { data } = await api.get("/legal-admin/entity-settings");
  return data;
}

export async function putEntitySettings(payload) {
  const { data } = await api.put("/legal-admin/entity-settings", payload);
  return data;
}
